import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { FiPlus, FiEdit, FiTrash2 } from "react-icons/fi";
import { useAppDispatch, useAppSelector } from "../../../../../store/hooks";
import Button from "../../../../../components/ui/Button";
import Card from "../../../../../components/ui/Card";
import { useTheme } from "../../../../../context/ThemeContext";
import ConfirmDeleteModal from "../../../../../components/ui/ConfirmDeleteModal";
import CurrencyFormModal from "./CurrencyFormModal";
import { addNotification } from "../../../../../store/slices/uiSlice";
import { useParams } from "react-router-dom";
import { Currency, CreateCurrencyInput, UpdateCurrencyInput } from "../../../../../types/currency";
import * as currencyService from "../../../../../services/currencyService";
import { selectCurrencies, selectCurrencyLoading } from "../../../../../store/selectors";
import { fetchCurrencies } from "../../../../../store/slices/currencySlice";

const CurrenciesSettings: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { theme } = useTheme();
  const dispatch = useAppDispatch();
  const { branchId } = useParams<{ branchId: string }>();
  const [isLoading, setIsLoading] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [currencyToDelete, setCurrencyToDelete] = useState<Currency | null>(null);
  const [editingCurrency, setEditingCurrency] = useState<Currency | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Get currencies from Redux
  const currencies = useAppSelector(selectCurrencies);
  const isLoadingCurrencies = useAppSelector(selectCurrencyLoading);

  // Load currencies on mount
  useEffect(() => {
    if (branchId) {
      dispatch(fetchCurrencies(branchId));
    }
  }, [branchId, dispatch]);

  const handleAddCurrency = () => {
    setEditingCurrency(null);
    setFormModalOpen(true);
  };
  const handleEditCurrency = (currency: Currency) => {
    setEditingCurrency(currency);
    setFormModalOpen(true);
  };
  const handleFormSubmit = async (
    data: CreateCurrencyInput | UpdateCurrencyInput
  ) => {
    setIsLoading(true);
    try {
      if (editingCurrency) {
        // Update existing currency
        await currencyService.updateCurrency(editingCurrency.id, data as UpdateCurrencyInput);

        dispatch(
          addNotification({
            type: "success",
            title: t('common.success'),
            message: t('currencies.updateSuccess'),
            duration: 3000,
          })
        );
      } else {
        // Create new currency
        await currencyService.createCurrency(
          data as CreateCurrencyInput
        );

        dispatch(
          addNotification({
            type: "success",
            title: t('common.success'),
            message: t('currencies.addSuccess'),
            duration: 3000,
          })
        );
      }

      // Refetch to get updated data from server
      if (branchId) {
        await dispatch(fetchCurrencies(branchId)).unwrap();
      }

      setFormModalOpen(false);
      setEditingCurrency(null);
    } catch (error) {
      console.error("Error submitting form:", error);
      dispatch(
        addNotification({
          type: "error",
          title: t('common.error'),
          message: editingCurrency
            ? t('currencies.updateError')
            : t('currencies.addError'),
          duration: 3000,
        })
      );
    } finally {
      setIsLoading(false);
    }
  };
  const handleDeleteClick = (currency: Currency) => {
    setCurrencyToDelete(currency);
    setDeleteModalOpen(true);
  };
  const handleConfirmDelete = async () => {
    if (!currencyToDelete) return;

    setIsDeleting(true);
    try {
      await currencyService.deleteCurrency(currencyToDelete.id);

      setDeleteModalOpen(false);
      setCurrencyToDelete(null);

      dispatch(
        addNotification({
          type: "success",
          title: t('common.success'),
          message: t('currencies.deleteSuccess'),
          duration: 3000,
        })
      );

      // Refetch to get updated data from server
      if (branchId) {
        await dispatch(fetchCurrencies(branchId)).unwrap();
      }
    } catch (error) {
      console.error("Error deleting currency:", error);
      dispatch(
        addNotification({
          type: "error",
          title: t('common.error'),
          message: t('currencies.deleteError'),
          duration: 3000,
        })
      );
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div
      className={`px-4 sm:px-6 lg:px-8 py-8 ${theme === "dark" ? "text-gray-100" : "text-gray-900"
        }`}
    >

      {/* Header */}
      <div className="mb-8 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">
            {t('currencies.title')}
          </h1>
          <p className="mt-2 text-gray-600 dark:text-gray-400">
            {t('currencies.subtitle')}
          </p>
        </div>
        <div>
          <Button
            onClick={handleAddCurrency}
            leftIcon={<FiPlus />}
            className="bg-primary hover:bg-primary/90 "
          >
            {t('currencies.addCurrency')}
          </Button>
        </div>
      </div>



      {/* Content */}
      {isLoadingCurrencies ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : currencies.length === 0 ? (
        <Card className="min-w-[100px] text-center py-12">
          <p className="text-gray-500 dark:text-gray-400 text-lg">
            {t('currencies.noCurrencies')}
          </p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-4">
          {[...currencies]
            .sort((a, b) => {
              // Base currency first, then alphabetically by name
              if (a.is_base === b.is_base) {
                return a.name.localeCompare(b.name);
              }
              return a.is_base ? -1 : 1;
            })
            .map((currency) => (
              <Card
                key={currency.id}
                className={` hover:shadow-lg transition-shadow flex flex-col h-full relative ${currency.is_base
                  ? "border-2 border-primary/50 bg-primary/5 dark:bg-primary/10"
                  : ""
                  }`}
              >
                {/* Base Currency Badge */}
                {currency.is_base && (
                  <div className="absolute -top-3 left-4 z-10">
                    <span className="inline-block px-3 py-1.5 bg-orange-500 text-white text-sm font-bold rounded-full shadow-md">
                      {t('currencies.baseCurrency')}
                    </span>
                  </div>
                )}

                <div className=" flex flex-col h-full">{/* card data */}
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                          {currency.name}
                        </h3>
                        <p className="text-sm text-gray-600 dark:text-gray-400">
                          {currency.iso}
                        </p>
                      </div>
                      {currency.symbol && (
                        <span className={`text-xl font-bold ${currency.is_base ? "text-primary" : "text-gray-400"}`}>
                          {currency.symbol}
                        </span>
                      )}
                    </div>

                    {currency.exchange_rate !== null && (
                      <p className={`text-sm mb-3 font-medium ${currency.is_base
                        ? "text-primary dark:text-orange-400"
                        : "text-gray-600 dark:text-gray-400"
                        }`}>
                        {t('currencies.exchangeRate')}: <span className="font-bold">{currency.exchange_rate}</span>
                      </p>
                    )}
                  </div>

                  {/* edit & delete buttons  */}
                  <div className={` mt-4 pt-4 border-t ${currency.is_base
                    ? "border-primary/30"
                    : "border-gray-200 dark:border-gray-700"
                    } flex gap-2`}>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEditCurrency(currency)}
                      leftIcon={<FiEdit className="w-4 h-4" />}
                    >
                      {t('common.edit')}
                    </Button>
                    {!currency.is_base && (
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => handleDeleteClick(currency)}
                        leftIcon={<FiTrash2 className="w-4 h-4" />}
                      >
                        {t('common.delete')}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            ))}
        </div>
      )}

      {/* Add/Edit Form Modal */}
      <CurrencyFormModal
        isOpen={formModalOpen}
        onClose={() => {
          setFormModalOpen(false);
          setEditingCurrency(null);
        }}
        onSubmit={handleFormSubmit}
        initialData={editingCurrency || undefined}
        isLoading={isLoading}
        branchId={branchId || ""}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmDeleteModal
        isOpen={deleteModalOpen}
        onCancel={() => {
          setDeleteModalOpen(false);
          setCurrencyToDelete(null);
        }}
        onConfirm={handleConfirmDelete}
        title={t('currencies.deleteCurrency')}
        message={`${t('common.delete')} ${currencyToDelete?.name}? ${t('currencies.deleteConfirmation')}`}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default CurrenciesSettings;
